"use client"

import Image from "next/image"
import Link from "next/link"
import { Button, ArrowRightIcon } from "@/components/ui/button"
import { ComingSoonPill } from "@/components/ui/coming-soon-pill"
import { blobMediaUrl } from "@/lib/blob-url"

interface PackageCard {
  image: { src: string; alt: string }
  eyebrow: string
  title: string
  description: string
  forLabel: string
  items: string[]
  priceFrom: string
  price: string
  ctaLabel: string
  href: string
  /** When set, renders a "coming soon" pill in the card's top-right corner
   *  (DE: the MRI packages aren't bookable yet). Already-translated label. */
  comingSoonLabel?: string
}

interface PackageSelectorProps {
  heading?: string
  description?: string
  /** Omitted in MRI-only markets (AT), where blood work is sold only inside
   *  the Advanced and Pro packages and never as its own product. */
  blood?: PackageCard
  /** Omitted in blood-only markets (DE), where the MRI packages aren't part of
   *  the offer at all. */
  mri?: PackageCard
  compareLabel?: string
  compareHref?: string
}

function Card({ card, delay: _delay }: { card: PackageCard; delay: number }) {
  return (
    <div className="relative h-full">
      {card.comingSoonLabel && (
        <ComingSoonPill
          label={card.comingSoonLabel}
          variant="light"
          className="absolute top-4 right-4 z-10 md:top-6 md:right-6"
        />
      )}
      <Link
        href={card.href}
        className="pricing-card flex h-full flex-col rounded-[12px] border border-white bg-white/[0.57] p-4 no-underline transition-[border-color] duration-300 md:p-10"
      >
        <Image
          src={card.image.src}
          width={184}
          height={184}
          className="h-[92px] w-[92px] rounded-[8px] object-cover"
          alt={card.image.alt}
        />
        <p className="type-title-sm text-aubergine-600 mt-6">{card.eyebrow}</p>
        <h3 className="type-headline-md mt-2 text-neutral-900">{card.title}</h3>
        <p className="type-body-lg mt-2 text-neutral-600">{card.description}</p>

        <p className="type-title-sm mt-8 text-neutral-900">{card.forLabel}</p>
        <ul className="mt-4 flex-1 space-y-2">
          {card.items.map((item) => (
            <li key={item} className="flex items-start gap-2">
              <Image
                src={blobMediaUrl("/images/check.svg")}
                alt=""
                width={16}
                height={16}
                className="mt-1 flex-shrink-0"
                aria-hidden="true"
              />
              <span className="type-body-lg text-neutral-900">{item}</span>
            </li>
          ))}
        </ul>

        {/* mt-auto keeps the CTA pinned to the bottom. The price itself is
            hidden when the locale supplies no price (e.g. NL while prices are
            withheld) — render nothing rather than a stray "from" label. */}
        <div className="mt-auto pt-8">
          {card.price ? (
            <>
              <span className="type-body-md text-neutral-600">
                {card.priceFrom}{" "}
              </span>
              <span className="type-headline-md text-neutral-900">
                {card.price}
              </span>
            </>
          ) : null}
        </div>
        <Button
          variant="outline"
          className="group relative mt-6 w-full self-stretch overflow-hidden md:w-auto md:self-start md:!px-10"
          iconRight={
            <span className="absolute right-4 translate-x-3 opacity-0 transition-all duration-200 ease-out group-hover:translate-x-0 group-hover:opacity-100">
              <ArrowRightIcon />
            </span>
          }
        >
          <span className="inline-block transition-transform duration-200 ease-out group-hover:-translate-x-2">
            {card.ctaLabel}
          </span>
        </Button>
      </Link>
    </div>
  )
}

export function PackageSelector({
  heading,
  description,
  blood,
  mri,
  compareLabel,
  compareHref,
}: PackageSelectorProps) {
  return (
    <section className="lazy-section py-16 md:py-24">
      {(heading || description) && (
        <div className="px-4 md:px-10">
          {heading && (
            <h2 className="type-display-md text-center text-neutral-900">
              {heading}
            </h2>
          )}
          {description && (
            <p className="type-body-lg mx-auto mt-4 max-w-2xl text-center text-neutral-600">
              {description}
            </p>
          )}
        </div>
      )}

      <div
        // One card on the single-modality homepages (blood-only DE, MRI-only
        // AT) is centred at the narrower width rather than left in a
        // two-column grid with a hole beside it.
        className={`mx-auto mt-12 grid grid-cols-1 items-stretch gap-6 px-4 md:px-10 ${
          blood && mri ? "max-w-5xl md:grid-cols-2" : "max-w-xl"
        }`}
      >
        {blood && <Card card={blood} delay={0} />}
        {mri && <Card card={mri} delay={blood ? 0.1 : 0} />}
      </div>

      {compareLabel && compareHref && (
        <div>
          <div className="mt-8 flex justify-center">
            <Link href={compareHref}>
              <Button variant="outline" className="group">
                {compareLabel}
                <ArrowRightIcon
                  size={18}
                  className="transition-transform duration-200 ease-out group-hover:translate-x-1"
                />
              </Button>
            </Link>
          </div>
        </div>
      )}
    </section>
  )
}
